/**
 * lib/dashboardDemo.js — preview-only demo mode.
 *
 * Provides fixture data to visualise the redesigned Dashboard sections
 * (particularly the FinancialBriefingHero tabbed slider) BEFORE the
 * Phase 2C ranker and Phase 2D briefing composer exist.
 *
 * Guardrails:
 *   • Client-side only. No backend or DB touchpoints.
 *   • Never enabled by default in Production. Even on Preview, users
 *     must opt in explicitly via URL param or localStorage.
 *   • The fixture shapes match the future Phase 2C/2D contracts declared
 *     in components/dashboard/types.js, so switching from demo → real
 *     data is a one-liner at the hook level.
 *
 * Toggle:
 *   URL param       ?demo=1  → enables + persists
 *                   ?demo=0  → disables + persists
 *   localStorage    '2120_dashboard_demo' = 'on' | (missing)
 *
 * When enabled the hook layer substitutes fixture data for the redesigned
 * sections. Real user data is never mutated.
 */

const LS_KEY = '2120_dashboard_demo';

export function readUrlParamAndPersist() {
  try {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    if (!params.has('demo')) return null;
    const v = params.get('demo');
    if (v === '1' || v === 'on' || v === 'true') {
      window.localStorage.setItem(LS_KEY, 'on');
      return true;
    }
    window.localStorage.removeItem(LS_KEY);
    return false;
  } catch {
    return null;
  }
}

export function isDemoEnabled() {
  try {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(LS_KEY) === 'on';
  } catch {
    return false;
  }
}

export function setDemoEnabled(on) {
  try {
    if (typeof window === 'undefined') return;
    if (on) window.localStorage.setItem(LS_KEY, 'on');
    else    window.localStorage.removeItem(LS_KEY);
  } catch { /* ignore */ }
}

/**
 * The four canonical briefing updates that back the tabbed hero in demo
 * mode. Each update includes its OWN Next Best Action (§2.4 of the
 * contract; the Figma hero binds NBAs to updates, not to a separate
 * standalone card).
 */
export const DEMO_BRIEFING_UPDATES = [
  {
    id: 'u-attention',
    category: 'attention',
    category_label: 'MOST IMPORTANT RIGHT NOW',
    tab_label: 'Internet bill +₹400',
    tab_icon: 'receipt',
    headline: 'Your internet payment increased by ₹400 this month.',
    sub_line: 'The recurring payment changed from ₹1,499 to ₹1,899.',
    body_line: 'If the new amount continues, this will add ₹4,800 to your annual committed spending.',
    bullets: ['Your total recurring commitments have increased for two consecutive months.'],
    provenance: 'H',
    next_best_action: {
      id: 'nba-attention',
      headline: 'Review the increased internet payment.',
      context: 'Check whether the price change is expected and whether the service still fits your priorities.',
      cost_note: 'If the new amount continues, the increase will cost ₹4,800 over the next year.',
      why_2120:  'You have a recurring commitment whose amount changed by more than 20% month-over-month. 2120 surfaces those changes so you can decide, not so the amount silently escalates.',
      cta:       { label: 'Review payment',   action: 'navigate', target: '/recurring' },
      dismiss:   { label: 'Not now' },
      provenance: 'D',
    },
  },
  {
    id: 'u-progress',
    category: 'rising',
    category_label: 'PROGRESS UPDATE',
    tab_label: 'Goal reached 57%',
    tab_icon: 'trend-up',
    headline: 'You kept ₹12,600 more of your income than last month.',
    sub_line: 'This is your third consecutive month of positive cash flow.',
    body_line: 'You are gradually creating more financial room while continuing to fund your Emergency Cushion.',
    bullets: ['Your Emergency Cushion is now 57% funded.'],
    provenance: 'D',
    next_best_action: {
      id: 'nba-progress',
      headline: 'Move ₹10,000 toward your Emergency Cushion.',
      context: 'Based on your recent cash flow and known upcoming commitments, this amount appears consistent with maintaining your usual financial buffer.',
      cost_note: 'This would move your Emergency Cushion from 57% to approximately 59% funded.',
      why_2120:  'A rule-based rerun of your last three months’ cash flow shows this contribution is consistent with your typical buffer. 2120 never proposes an amount larger than the surplus your history supports.',
      cta:       { label: 'Review this action', action: 'navigate', target: '/goals' },
      dismiss:   { label: 'Not now' },
      provenance: 'D',
    },
  },
  {
    id: 'u-opportunity',
    category: 'opportunity',
    category_label: 'OPPORTUNITY',
    tab_label: '₹2,850 opportunity',
    tab_icon: 'compass',
    headline: 'You could create ₹2,850 more room each month.',
    sub_line: 'Three recurring commitments have increased over the last six months and have not been reviewed.',
    body_line: 'Redirecting even part of these could accelerate your Emergency Cushion.',
    bullets: ['Two of the three are subscription-style services.'],
    provenance: 'D',
    next_best_action: {
      id: 'nba-opportunity',
      headline: 'Review three recurring commitments.',
      context: 'Confirm whether each is still worth its current price. No action is required if they are.',
      cost_note: null,
      why_2120:  'These are commitments whose amounts have drifted upward without any explicit review event in the last six months. Surfacing them is a factual observation, not a judgement.',
      cta:       { label: 'Open Recurring', action: 'navigate', target: '/recurring' },
      dismiss:   { label: 'Not now' },
      provenance: 'D',
    },
  },
  {
    id: 'u-upcoming',
    category: 'upcoming',
    category_label: 'UPCOMING',
    tab_label: '₹42K upcoming',
    tab_icon: 'clock',
    headline: '₹42,000 of payments are due in the next 14 days.',
    sub_line: 'Rent, credit card, and one SIP together represent about 34% of last month’s income.',
    body_line: 'Your buffer covers this comfortably given your current liquid balance.',
    bullets: ['No payment in this window is auto-debited from an under-funded account.'],
    provenance: 'F',
    next_best_action: {
      id: 'nba-upcoming',
      headline: 'Confirm your upcoming payments look right.',
      context: 'A quick scan today avoids surprises later this week.',
      cost_note: null,
      why_2120:  'These are the confirmed recurring commitments whose next_due dates fall inside the next 14 days. No amount has been inferred; every value shown is what you have already declared.',
      cta:       { label: 'Open Recurring', action: 'navigate', target: '/recurring' },
      dismiss:   { label: 'Not now' },
      provenance: 'F',
    },
  },
];

/** Demo attention rows so the Needs Your Attention card also demonstrates. */
export const DEMO_ATTENTION_ITEMS = [
  {
    id: 'demo-payment-1',
    kind: 'payment_due',
    title: 'Rent due Jul 18 – ₹28,000',
    sub_line: 'Recurring · monthly',
    severity: 'medium',
    drawer_type: 'attention-payment',
    linked_id: 'demo-rent',
    provenance: 'F',
    source_data: {
      id: 'demo-rent', name: 'Rent',
      monthly_equivalent_base: 28000,
      frequency: 'monthly', next_due: '2026-07-18',
    },
  },
  {
    id: 'demo-recurring-1',
    kind: 'recurring_suggestion',
    title: '2 new recurring patterns detected',
    sub_line: 'Confirm or dismiss on the Recurring page',
    severity: 'low',
    drawer_type: 'attention-recurring',
    source_data: { merchant: 'Netflix', cadence: 'monthly' },
    provenance: 'D',
  },
  {
    id: 'demo-recon-1',
    kind: 'reconciliation',
    title: '3 duplicate candidates to review',
    sub_line: 'From your recent imports',
    severity: 'medium',
    drawer_type: null,
    cta: { label: 'Review', action: 'navigate', target: '/review-uncategorized' },
    provenance: 'D',
  },
];

export const DEMO_SNAPSHOT_PATCH = {
  identity: { base_currency: 'INR', display_name: 'Alex' },
  derived: { savings_rate_pct: 34.2, runway_months: 8.4 },
  cashflow: { surplus_monthly: 42500 },
  commitments: { total_monthly_out_base: 62400, items: [
    { id: 'demo-rent',   active: true, kind: 'expense',      name: 'Rent',      monthly_equivalent_base: 28000, next_due: '2026-07-18', frequency: 'monthly' },
    { id: 'demo-netfl',  active: true, kind: 'expense',      name: 'Netflix',   monthly_equivalent_base:  649,  next_due: '2026-07-21', frequency: 'monthly' },
    { id: 'demo-sip',    active: true, kind: 'investment',   name: 'SIP',       monthly_equivalent_base: 15000, next_due: '2026-07-25', frequency: 'monthly' },
    { id: 'demo-cc',     active: true, kind: 'debt_payment', name: 'Credit Card', monthly_equivalent_base: 12500, next_due: '2026-07-22', frequency: 'monthly' },
  ]},
  goals: {
    items: [{
      id: 'demo-emergency', title: 'Emergency Cushion', kind: 'custom',
      current_amount_base: 171000, target_amount_base: 300000, progress_pct: 57,
      target_date: '2027-03-31', on_track: true, priority: 'high',
      monthly_contribution_needed_base: 10500,
    }],
    top_priority_goal_id: 'demo-emergency',
  },
  derived_committed_pct: 46,
  flags: { has_transactions: true },
};

export const DEMO_BREAKDOWN_THIS_MONTH = {
  base_currency: 'INR', total_income: 125000, total_expenses: 82500, total_surplus: 42500,
  currencies_in_use: ['INR'], stale_days: 0,
};
export const DEMO_BREAKDOWN_PRIOR = {
  base_currency: 'INR', total_income: 118000, total_expenses: 88100, total_surplus: 29900,
  currencies_in_use: ['INR'], stale_days: 0,
};

export const DEMO_TREND_6M = [
  { ym: '2026-02', label: 'Feb', income_base: 110000, expenses_base: 92000,  money_kept_base: 18000 },
  { ym: '2026-03', label: 'Mar', income_base: 114500, expenses_base: 89400,  money_kept_base: 25100 },
  { ym: '2026-04', label: 'Apr', income_base: 138000, expenses_base: 88000,  money_kept_base: 15200 },
  { ym: '2026-05', label: 'May', income_base: 140000, expenses_base: 85000,  money_kept_base: 17800 },
  { ym: '2026-06', label: 'Jun', income_base: 128500, expenses_base: 82000,  money_kept_base: 19200 },
  { ym: '2026-07', label: 'Jul', income_base: 142000, expenses_base: 72400,  money_kept_base: 21500 },
];

export const DEMO_TREND_12M = [
  { ym: '2025-08', label: 'Aug', income_base: 118000, expenses_base: 90500, money_kept_base: 12000 },
  { ym: '2025-09', label: 'Sep', income_base: 121000, expenses_base: 92800, money_kept_base: 13400 },
  { ym: '2025-10', label: 'Oct', income_base: 116500, expenses_base: 91200, money_kept_base: 11800 },
  { ym: '2025-11', label: 'Nov', income_base: 125000, expenses_base: 94000, money_kept_base: 14200 },
  { ym: '2025-12', label: 'Dec', income_base: 135000, expenses_base: 98000, money_kept_base: 15800 },
  { ym: '2026-01', label: 'Jan', income_base: 128000, expenses_base: 88500, money_kept_base: 16500 },
  ...[
    { ym: '2026-02', label: 'Feb', income_base: 110000, expenses_base: 92000, money_kept_base: 18000 },
    { ym: '2026-03', label: 'Mar', income_base: 114500, expenses_base: 89400, money_kept_base: 25100 },
    { ym: '2026-04', label: 'Apr', income_base: 138000, expenses_base: 88000, money_kept_base: 15200 },
    { ym: '2026-05', label: 'May', income_base: 140000, expenses_base: 85000, money_kept_base: 17800 },
    { ym: '2026-06', label: 'Jun', income_base: 128500, expenses_base: 82000, money_kept_base: 19200 },
    { ym: '2026-07', label: 'Jul', income_base: 142000, expenses_base: 72400, money_kept_base: 21500 },
  ],
];

/**
 * Committed Money category breakdown (Amendment 6: category comes from an
 * authoritative source — in demo we use a hand-authored fixture; in
 * production this will be the recurring/kind field aggregated by category).
 */
export const DEMO_COMMITTED_CATEGORIES = [
  { key: 'home_loan',     label: 'Home Loan',     amount_base: 14500, color: 'hsl(var(--primary))'   },
  { key: 'insurance',     label: 'Insurance',     amount_base:  6200, color: 'hsl(var(--emerald))'   },
  { key: 'subscriptions', label: 'Subscriptions', amount_base:  4100, color: 'hsl(var(--sage))'      },
  { key: 'utilities',     label: 'Utilities',     amount_base:  3800, color: 'hsl(var(--gold))'      },
  { key: 'other',         label: 'Other',         amount_base:  3200, color: 'hsl(var(--muted))'     },
];

export const DEMO_COMMITTED_NOTE = "You have 3 recurring expenses you haven't reviewed in 6 months.";

/** Spending breakdown drawer fixture (Position Card → \"What changed\"). */
export const DEMO_SPENDING_BREAKDOWN = {
  this_month_base: 72400,
  prior_month_base: 80820,
  delta_base: -8420,
  what_changed_text: "Your total spending this month was ₹72,400 — down ₹8,420 from June. The biggest driver was a reduction in travel spending, which fell from ₹18,200 to ₹6,800.",
  categories: [
    { label: 'Travel',           delta_base: -11400, direction: 'down' },
    { label: 'Dining',           delta_base:  -2100, direction: 'down' },
    { label: 'Home Maintenance', delta_base:  18400, direction: 'up'   },
    { label: 'Groceries',        delta_base:  -1200, direction: 'down' },
    { label: 'Transport',        delta_base:    920, direction: 'up'   },
  ],
};

/** Monthly contributions for the demo goal (rendered in the goal drawer). */
export const DEMO_GOAL_MONTHLY_CONTRIBS = [
  { label: 'May', amount_base: 17800 },
  { label: 'Jun', amount_base: 19200 },
  { label: 'Jul', amount_base: 21500 },
];

export const DEMO_REFLECTION_QUESTION = {
  id: 'demo-reflection',
  question_text: 'What one commitment do you most want to review this month?',
  subtext: 'Take a minute to write down which one, and why.',
  question_category: 'weekly',
};

/** Demo previous-month reflections shown on the Monthly Reflection card. */
export const DEMO_PRIOR_REFLECTIONS = [
  {
    id: 'demo-prior-1',
    week_ending: '2026-06-28',
    answer_text: 'I noticed I was ordering food more often on Fridays after long weeks. I want to pre-plan those Fridays instead of falling into a habit that costs ₹3,000 a month.',
  },
  {
    id: 'demo-prior-2',
    week_ending: '2026-06-14',
    answer_text: 'Renewing the gym felt automatic. I want to check what I actually used it for before renewing again in July.',
  },
];

/**
 * 2120's factual read of the previous calendar month — authored by the
 * Phase 2D briefing composer (provenance 'H'). Fixture only.
 */
export const DEMO_PRIOR_MONTH_RECAP = {
  title: 'June was a stronger month financially.',
  body: 'Spending decreased, you kept more of your income, and you continued making progress toward your Emergency Cushion. Your recurring commitments held steady, and no unusual outflows appeared in the second half of the month.',
  provenance: 'H',
};

/**
 * Demo biggest opportunity for the current month. In production this comes
 * from the Phase 2D briefing composer with provenance 'H'. Marked as demo-
 * only here so the copy is obviously placeholder.
 */
export const DEMO_BIGGEST_OPPORTUNITY = {
  title: 'Redirect ₹2,850 of drifting subscriptions toward your Emergency Cushion.',
  body: 'Three recurring commitments have crept upward in the last six months without being reviewed. Reviewing them is a small act that compounds over the rest of the year.',
  provenance: 'H',
};

